import { useEffect, useState } from 'react';
import { ClipboardList, ExternalLink } from 'lucide-react';
import { authFetch } from '../lib/auth-fetch';

// Status-Meta für die Anzeige im kompakten Badge / in der Aufgaben-Liste.
// Quelle: TasksPage.LANES (todo, doing, review, done) — bewusst dupliziert,
// damit dieses Panel keine Abhängigkeit zur TasksPage hat (es wird im
// OrderHistoryModal und in InquiriesPage eingebunden).
const STATUS_META = {
  todo:   { label: 'Offen',     pill: 'bg-red-50 text-red-700 border border-red-200' },
  doing:  { label: 'In Arbeit', pill: 'bg-yellow-50 text-yellow-700 border border-yellow-200' },
  review: { label: 'Prüfung',   pill: 'bg-amber-50 text-amber-700 border border-amber-200' },
  done:   { label: 'Erledigt',  pill: 'bg-green-50 text-green-700 border border-green-200' },
};

/**
 * Kompakte Aufgaben-Übersicht für die Detail-Ansicht von Aufträgen und
 * Anfragen. Lädt alle Aufgaben mit `sourceId === id` (ohne `mine`-Filter)
 * und zeigt:
 *   • Status-Zähler oben (z. B. „2 Offen · 1 In Arbeit · 3 Erledigt")
 *   • kompakte Liste der Aufgaben mit Status-Pill + Fälligkeit + Titel
 *   • Klick auf Eintrag → zur Aufgaben-Seite
 *
 * Props:
 *   sourceId  – Order- oder Inquiry-ID
 *   title     – Optional, Standard „Verknüpfte Aufgaben"
 *   emptyHint – Optional, Standard „Noch keine Aufgaben verknüpft."
 *   compact   – wenn true: nur die Status-Zähler ohne Listenausgabe
 */
export default function LinkedTasksPanel({ sourceId, title = 'Verknüpfte Aufgaben', emptyHint = 'Noch keine Aufgaben verknüpft.', compact = false }) {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sourceId) { setTasks([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const r = await authFetch(`/api/tasks?sourceId=${encodeURIComponent(sourceId)}&mine=false`);
      if (cancelled) return;
      if (r.ok) setTasks(await r.json()); else setTasks([]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sourceId]);

  if (loading) {
    return (
      <div className="text-[11px] text-gray-400 italic" data-testid="linked-tasks-loading">
        Lade Aufgaben …
      </div>
    );
  }

  const counts = (tasks || []).reduce((acc, t) => {
    const k = t.status in STATUS_META ? t.status : 'todo';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const total = (tasks || []).length;

  if (total === 0) {
    return (
      <div className="text-[11px] text-gray-400 italic" data-testid="linked-tasks-empty">
        {emptyHint}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="linked-tasks-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium inline-flex items-center gap-1">
          <ClipboardList className="w-3 h-3" />{title} ({total})
        </div>
        {Object.entries(STATUS_META).map(([k, meta]) => (
          counts[k] ? (
            <span key={k} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${meta.pill}`} data-testid={`linked-tasks-count-${k}`}>
              {counts[k]} {meta.label}
            </span>
          ) : null
        ))}
      </div>
      {!compact && (
        <ul className="space-y-1.5">
          {(tasks || []).slice(0, 8).map(t => {
            const meta = STATUS_META[t.status] || STATUS_META.todo;
            return (
              <li key={t.id}>
                <a
                  href={`/dashboard/aufgaben?task=${encodeURIComponent(t.id)}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-gray-200 hover:border-[#9ec043] hover:bg-[#9ec043]/5 transition group"
                  data-testid={`linked-task-${t.id}`}
                  title={t.description || t.title}
                >
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${meta.pill} shrink-0`}>
                    {meta.label}
                  </span>
                  <span className="text-xs text-gray-700 flex-1 truncate">{t.title}</span>
                  {t.dueDate && (
                    <span className="text-[10px] text-gray-400 shrink-0">
                      fällig {new Date(t.dueDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                    </span>
                  )}
                  <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-[#9ec043] shrink-0" />
                </a>
              </li>
            );
          })}
          {total > 8 && (
            <li className="text-[10px] text-gray-400 text-center pt-1">
              + {total - 8} weitere — siehe Aufgaben-Seite
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
